import { useMemo, useState, useEffect } from 'react';
import { Link, useParams } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Filter, Grid3X3, List, Search, SlidersHorizontal } from 'lucide-react';
import type { Category } from '@shared/schema';
import type { PaginatedResponse } from '@shared/api-contracts';

import { PublicShell } from '@/components/public-shell';
import { fetchJson } from '@/lib/queryClient';
import ClassCard from '@/components/class-card';
import { ComingSoonBanner } from '@/components/coming-soon-banner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useResponsiveViewport } from '@/hooks/useResponsiveViewport';
import { useRegionScope } from '@/hooks/useRegionScope';
import {
  buildBrowseHref,
  buildReturnToTarget,
  readCanonicalBrowseState,
  updateBrowseUrl,
  useBrowseScrollRestoration,
} from '@/lib/browse-state';
import { formatSessionTime } from '@/lib/geography';
import { AutoRedirect } from '@/components/auto-redirect';

const LIMIT = 12;

type SortOption = 'popular' | 'newest' | 'price_asc' | 'price_desc';
type ViewMode = 'grid' | 'list';
type DeliveryModeFilter = 'all' | 'in_person' | 'online' | 'hybrid';
type PriceRangeFilter = 'all' | 'free' | 'under_25' | '25_to_75' | 'over_75';

interface ClassRecord {
  id: string;
  title: string;
  description: string;
  price: string;
  pricingMode?: string | null;
  suggestedPrice?: string | null;
  promoEnabled?: boolean | null;
  promoPrice?: string | null;
  thumbnailUrl?: string | null;
  deliveryMode?: string | null;
  enrollmentCount?: number;
  spotsLeft?: number | null;
  creator?: { id: string; displayName: string; profileImageUrl?: string | null } | null;
  category?: string | null;
  localityName?: string | null;
  venue?: {
    id?: string | null;
    name?: string | null;
    slug?: string | null;
    address?: string | null;
  } | null;
  nextSessionStart?: string | null;
  nextSessionEnd?: string | null;
}

const DELIVERY_MODE_OPTIONS: { value: DeliveryModeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online' },
  { value: 'hybrid', label: 'Hybrid' },
];

const PRICE_RANGE_OPTIONS: { value: PriceRangeFilter; label: string }[] = [
  { value: 'all', label: 'All prices' },
  { value: 'free', label: 'Free' },
  { value: 'under_25', label: 'Under $25' },
  { value: '25_to_75', label: '$25 – $75' },
  { value: 'over_75', label: '$75+' },
];

function FiltersPanel({
  categories,
  query,
  setQuery,
  selectedCategory,
  setSelectedCategory,
  deliveryModeFilter,
  setDeliveryModeFilter,
  priceRangeFilter,
  setPriceRangeFilter,
  sort,
  setSort,
  onApply,
  onReset,
}: {
  categories: Category[];
  query: string;
  setQuery: (value: string) => void;
  selectedCategory: string;
  setSelectedCategory: (value: string) => void;
  deliveryModeFilter: DeliveryModeFilter;
  setDeliveryModeFilter: (value: DeliveryModeFilter) => void;
  priceRangeFilter: PriceRangeFilter;
  setPriceRangeFilter: (value: PriceRangeFilter) => void;
  sort: SortOption;
  setSort: (value: SortOption) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  return (
    <div className="space-y-5">
      <div>
        <label className="editorial-kicker">Search</label>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={event => setQuery(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                onApply();
              }
            }}
            placeholder="Watercolor, ceramics, bread baking..."
            className="touch-target rounded-full pl-10"
          />
        </div>
      </div>

      <div>
        <label className="editorial-kicker">Category</label>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSelectedCategory('')}
            className={`touch-target rounded-full px-4 text-sm ${selectedCategory === '' ? 'bg-foreground text-background' : 'border border-border/70 bg-card text-foreground'}`}
          >
            All
          </button>
          {categories.map(category => {
            const active = selectedCategory === category.id;
            return (
              <button
                type="button"
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`touch-target rounded-full px-4 text-sm ${active ? 'bg-foreground text-background' : 'border border-border/70 bg-card text-foreground'}`}
              >
                {category.name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="editorial-kicker">Delivery</label>
        <div className="mt-3 flex flex-wrap gap-2">
          {DELIVERY_MODE_OPTIONS.map(opt => (
            <button
              type="button"
              key={opt.value}
              onClick={() => setDeliveryModeFilter(opt.value)}
              className={`touch-target rounded-full px-4 text-sm ${deliveryModeFilter === opt.value ? 'bg-foreground text-background' : 'border border-border/70 bg-card text-foreground'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="editorial-kicker">Price</label>
        <div className="mt-3 flex flex-wrap gap-2">
          {PRICE_RANGE_OPTIONS.map(opt => (
            <button
              type="button"
              key={opt.value}
              onClick={() => setPriceRangeFilter(opt.value)}
              className={`touch-target rounded-full px-4 text-sm ${priceRangeFilter === opt.value ? 'bg-foreground text-background' : 'border border-border/70 bg-card text-foreground'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="editorial-kicker">Sort</label>
        <select
          value={sort}
          onChange={event => setSort(event.target.value as SortOption)}
          className="touch-target mt-2 w-full rounded-full border border-border/70 bg-card px-4 text-sm"
        >
          <option value="popular">Most popular</option>
          <option value="newest">Newest</option>
          <option value="price_asc">Price: low to high</option>
          <option value="price_desc">Price: high to low</option>
        </select>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button className="touch-target rounded-full px-5" onClick={onApply}>
          Apply filters
        </Button>
        <Button variant="outline" className="touch-target rounded-full px-5" onClick={onReset}>
          Reset
        </Button>
      </div>
    </div>
  );
}

export default function RegionClassesBrowse() {
  const { regionSlug, localitySlug } = useParams<{ regionSlug: string; localitySlug?: string }>();
  const { region, locality, isLoading, isError } = useRegionScope(regionSlug, localitySlug);
  const { isPhone, isTablet, isDesktop } = useResponsiveViewport();
  const initialBrowseState = useMemo(() => readCanonicalBrowseState(window.location.search), []);

  const [query, setQuery] = useState(initialBrowseState.q ?? '');
  const [appliedQuery, setAppliedQuery] = useState(initialBrowseState.q ?? '');
  const [selectedCategory, setSelectedCategory] = useState(initialBrowseState.category ?? '');
  const [deliveryModeFilter, setDeliveryModeFilter] = useState<DeliveryModeFilter>('all');
  const [priceRangeFilter, setPriceRangeFilter] = useState<PriceRangeFilter>('all');
  const [sort, setSort] = useState<SortOption>(
    (initialBrowseState.sort as SortOption) ?? 'popular'
  );
  const [view, setView] = useState<ViewMode>(
    (initialBrowseState.view as ViewMode) ?? (isDesktop ? 'grid' : 'list')
  );
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!initialBrowseState.view) {
      setView(isDesktop ? 'grid' : 'list');
    }
  }, [initialBrowseState.view, isDesktop]);

  const timezone = locality?.timezone ?? region?.timezone ?? 'America/Indiana/Indianapolis';
  const basePath =
    region && locality
      ? `/regions/${region.slug}/${locality.slug}`
      : region
        ? `/regions/${region.slug}`
        : '/';
  const apiBase =
    region && locality
      ? `/api/regions/${region.slug}/${locality.slug}`
      : region
        ? `/api/regions/${region.slug}`
        : '/api/regions';

  const browseState = useMemo(
    () => ({
      q: appliedQuery || undefined,
      category: selectedCategory || undefined,
      sort,
      view,
    }),
    [appliedQuery, selectedCategory, sort, view]
  );

  useEffect(() => {
    if (region) {
      updateBrowseUrl(window.location.pathname, browseState);
    }
  }, [browseState, region]);

  const currentSearch = buildBrowseHref(window.location.pathname, browseState).split('?')[1] ?? '';
  useBrowseScrollRestoration(
    window.location.pathname,
    currentSearch ? `?${currentSearch}` : '',
    !isLoading
  );

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ['/api/categories'],
    queryFn: () => fetchJson<Category[]>('/api/categories'),
    staleTime: 1000 * 60 * 60,
  });

  const { data: response, isLoading: classesLoading } = useQuery<PaginatedResponse<ClassRecord>>({
    queryKey: [
      'region-classes-browse',
      region?.slug,
      locality?.slug,
      appliedQuery,
      selectedCategory,
      deliveryModeFilter,
      priceRangeFilter,
      sort,
      page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(LIMIT),
        page: String(page),
        sortBy: sort,
      });
      if (appliedQuery) {
        params.set('search', appliedQuery);
      }
      if (selectedCategory) {
        params.set('category', selectedCategory);
      }
      if (deliveryModeFilter !== 'all') {
        params.set('deliveryMode', deliveryModeFilter);
      }
      if (priceRangeFilter !== 'all') {
        params.set('priceRange', priceRangeFilter);
      }

      return fetchJson<PaginatedResponse<ClassRecord>>(
        `${apiBase}/classes?${params.toString()}`
      );
    },
    enabled: !!region,
    staleTime: 1000 * 60 * 5,
  });

  if (isLoading) {
    return (
      <PublicShell routeKey="classes">
        <div className="page-shell flex min-h-[60vh] items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </PublicShell>
    );
  }

  if (isError || !region) {
    return (
      <PublicShell routeKey="not-found">
        <div className="page-shell-narrow flex min-h-[60vh] flex-col items-center justify-center gap-4 text-center">
          <h1 className="text-4xl font-semibold">We couldn’t load this class browse view.</h1>
          <AutoRedirect />
          <Link href="/">
            <Button className="rounded-full px-5">Choose another market</Button>
          </Link>
        </div>
      </PublicShell>
    );
  }

  const classes = response?.data ?? [];
  const total = response?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const displayName = locality?.name ?? region.name;
  const isComingSoon = locality?.slug === 'bloomington';
  const noResults = !classesLoading && classes.length === 0;
  const showDesktopRail = !isPhone && !isTablet;
  const activeChips = [
    appliedQuery ? { label: `”${appliedQuery}”`, onClear: () => setAppliedQuery('') } : null,
    selectedCategory
      ? {
          label: categories.find(category => category.id === selectedCategory)?.name ?? 'Category',
          onClear: () => setSelectedCategory(''),
        }
      : null,
    deliveryModeFilter !== 'all'
      ? {
          label:
            DELIVERY_MODE_OPTIONS.find(o => o.value === deliveryModeFilter)?.label ??
            deliveryModeFilter,
          onClear: () => setDeliveryModeFilter('all'),
        }
      : null,
    priceRangeFilter !== 'all'
      ? {
          label:
            PRICE_RANGE_OPTIONS.find(o => o.value === priceRangeFilter)?.label ?? priceRangeFilter,
          onClear: () => setPriceRangeFilter('all'),
        }
      : null,
  ].filter(Boolean) as Array<{ label: string; onClear: () => void }>;

  const returnTo = encodeURIComponent(
    buildReturnToTarget(window.location.pathname, window.location.search)
  );

  const filtersPanel = (
    <FiltersPanel
      categories={categories}
      query={query}
      setQuery={value => setQuery(value)}
      selectedCategory={selectedCategory}
      setSelectedCategory={value => setSelectedCategory(value)}
      deliveryModeFilter={deliveryModeFilter}
      setDeliveryModeFilter={value => setDeliveryModeFilter(value)}
      priceRangeFilter={priceRangeFilter}
      setPriceRangeFilter={value => setPriceRangeFilter(value)}
      sort={sort}
      setSort={value => setSort(value)}
      onApply={() => {
        setAppliedQuery(query);
        setPage(1);
      }}
      onReset={() => {
        setQuery('');
        setAppliedQuery('');
        setSelectedCategory('');
        setDeliveryModeFilter('all');
        setPriceRangeFilter('all');
        setSort('popular');
        setPage(1);
      }}
    />
  );

  return (
    <PublicShell routeKey="classes">
      <section className="hero-gradient border-b border-border/60">
        <div className="page-shell grid gap-8 py-12 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
          <div>
            <p className="editorial-kicker">
              {region.type === 'online' ? 'Live online classes' : `Classes in ${displayName}`}
            </p>
            <h1 className="section-title mt-4">
              {region.type === 'online'
                ? 'Online classes worth opening tonight.'
                : `Classes in ${displayName}`}
            </h1>
            <p className="section-copy mt-5 max-w-2xl">
              {region.type === 'online'
                ? 'Browse live sessions from independent creators. Filter by topic, price, or category and register in a few taps.'
                : `Filter by category, search by topic, and find a session that fits your schedule. Every class is led by an approved local creator.`}
            </p>
          </div>

          <Card className="glass-panel rounded-[2rem] border-border/60 shadow-lg">
            <CardContent className="space-y-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-primary/10 px-3 py-1 text-primary">
                  {total} results
                </Badge>
                <Badge variant="outline" className="rounded-full px-3 py-1">
                  {view}
                </Badge>
              </div>
              <p className="text-sm leading-6 text-muted-foreground">
                Showing classes in {displayName}. Use filters to narrow by category, delivery mode,
                or price.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="page-section">
        <div className="page-shell grid gap-8 xl:grid-cols-[18rem_minmax(0,1fr)]">
          {showDesktopRail ? (
            <aside className="sticky top-[calc(var(--shell-header-height)+1.5rem)] h-fit rounded-[1.75rem] border border-border/70 bg-card p-5 shadow-sm">
              {filtersPanel}
            </aside>
          ) : null}

          <div className="space-y-5">
            {isComingSoon ? <ComingSoonBanner kind="classes" localityName={displayName} /> : null}

            <div className="flex flex-col gap-4 rounded-[1.75rem] border border-border/70 bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap gap-2">
                {activeChips.length > 0 ? (
                  activeChips.map(chip => (
                    <button
                      type="button"
                      key={chip.label}
                      onClick={() => {
                        chip.onClear();
                        setPage(1);
                      }}
                      className="touch-target rounded-full border border-border/70 px-4 text-sm"
                    >
                      {chip.label} ×
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No active filters yet.</p>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {!showDesktopRail ? (
                  <Sheet>
                    <SheetTrigger asChild>
                      <Button variant="outline" className="touch-target rounded-full px-4">
                        <Filter className="mr-2 h-4 w-4" />
                        Filters
                      </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="max-h-[85dvh] rounded-t-[2rem]">
                      <SheetHeader>
                        <SheetTitle>Refine classes</SheetTitle>
                      </SheetHeader>
                      <div className="mt-6">{filtersPanel}</div>
                    </SheetContent>
                  </Sheet>
                ) : null}

                <div className="inline-flex rounded-full border border-border/70 bg-muted/50 p-1">
                  <button
                    type="button"
                    aria-label="Grid view"
                    onClick={() => setView('grid')}
                    className={`touch-target rounded-full px-3 ${view === 'grid' ? 'bg-background shadow-sm' : ''}`}
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="List view"
                    onClick={() => setView('list')}
                    className={`touch-target rounded-full px-3 ${view === 'list' ? 'bg-background shadow-sm' : ''}`}
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {classesLoading ? (
              <div className="grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div
                    key={index}
                    className="h-72 animate-pulse rounded-[1.75rem] border border-border/70 bg-card"
                  />
                ))}
              </div>
            ) : noResults && isComingSoon ? null : noResults ? (
              <Card className="rounded-[1.75rem] border-border/70 shadow-sm">
                <CardContent className="space-y-4 p-6">
                  <div className="flex items-center gap-3">
                    <SlidersHorizontal className="h-5 w-5 text-primary" />
                    <h2 className="text-2xl font-semibold">No classes match this filter set</h2>
                  </div>
                  <p className="text-sm leading-6 text-muted-foreground">
                    Try removing filters or browsing creators from this area instead.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      className="rounded-full px-5"
                      onClick={() => {
                        setQuery('');
                        setAppliedQuery('');
                        setSelectedCategory('');
                        setSort('popular');
                        setPage(1);
                      }}
                    >
                      Clear filters
                    </Button>
                    <Link href={`${basePath}/creators`}>
                      <Button variant="outline" className="rounded-full px-5">
                        Browse creators instead
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div
                className={
                  view === 'grid'
                    ? 'grid gap-5 grid-cols-[repeat(auto-fill,minmax(18rem,1fr))]'
                    : 'grid gap-5'
                }
              >
                {classes.map(classItem => (
                  <ClassCard
                    key={classItem.id}
                    id={classItem.id}
                    title={classItem.title}
                    description={classItem.description}
                    price={classItem.price}
                    pricingMode={classItem.pricingMode}
                    suggestedPrice={classItem.suggestedPrice}
                    promoEnabled={classItem.promoEnabled}
                    promoPrice={classItem.promoPrice}
                    thumbnailUrl={classItem.thumbnailUrl}
                    category={classItem.category}
                    deliveryMode={classItem.deliveryMode}
                    creator={classItem.creator}
                    enrollmentCount={classItem.enrollmentCount}
                    spotsLeft={classItem.spotsLeft}
                    localityName={classItem.localityName ?? displayName}
                    venue={classItem.venue}
                    venueHref={
                      localitySlug && classItem.venue?.slug
                        ? `${basePath}/locations/${classItem.venue.slug}`
                        : null
                    }
                    sessionLabel={
                      classItem.nextSessionStart
                        ? formatSessionTime(
                            classItem.nextSessionStart,
                            classItem.nextSessionEnd ?? classItem.nextSessionStart,
                            timezone
                          )
                        : null
                    }
                    variant={view === 'grid' ? (isDesktop ? 'comfortable' : 'compact') : 'expanded'}
                    detailHref={`/classes/${classItem.id}?returnTo=${returnTo}`}
                  />
                ))}
              </div>
            )}

            {totalPages > 1 ? (
              <div className="flex items-center justify-between rounded-[1.5rem] border border-border/70 bg-card p-4 shadow-sm">
                <p className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="rounded-full px-5"
                    disabled={page <= 1}
                    onClick={() => setPage(previous => Math.max(1, previous - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    className="rounded-full px-5"
                    disabled={page >= totalPages}
                    onClick={() => setPage(previous => Math.min(totalPages, previous + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
